import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AccountService, AccountType } from './account.service';
import { FirmApiService } from './firm-api.service';

interface AuthEnvelope {
  data?: {
    accessToken?: string;
    csrf_token?: string;
    user?: { _id?: string; name?: string; email?: string };
    session?: { _id?: string };
    // Step-1 sign-in on a 2FA-enabled account: no session is established yet.
    requiresTwoFactor?: boolean;
    email?: string;
    loginChallenge?: string;
    codeExpiresIn?: number;
  };
  metadata?: { message?: string };
}

/** Step-1 credentials accepted, but the account has 2FA on — an emailed code is pending. */
export interface TwoFactorChallenge {
  email: string;
  loginChallenge: string;
  /** Seconds the emailed code stays valid — drives the client countdown. */
  codeExpiresIn: number;
}

/**
 * What `login()` resolved to. A 2FA account stops at step 1 with a challenge; everything else is
 * signed in and the firm context is already loaded.
 */
export type LoginOutcome =
  | { signedIn: true }
  | { signedIn: false; challenge: TwoFactorChallenge };

/** sessionStorage key carrying the pending 2FA challenge between sign-in step 1 and step 2. */
const TWO_FACTOR_KEY = 'acc_2fa_challenge';

/** Fallback code lifetime when the server sends none — matches the backend's 3-minute OTP TTL. */
export const DEFAULT_CODE_TTL_SECONDS = 180;

/**
 * Parse a server-provided code TTL (seconds) defensively: accepts either `codeExpiresIn` or
 * `code_expires_in`, coerces numeric strings, and falls back to the default when the value isn't a
 * finite positive number — so the countdown can never render NaN.
 */
function resolveCodeTtl(data: Record<string, unknown> | undefined): number {
  const value = Number(data?.['codeExpiresIn'] ?? data?.['code_expires_in']);
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_CODE_TTL_SECONDS;
}

/**
 * Real auth for the accountant app against the shared backend. Auth rides the httpOnly session
 * cookie + a double-submit CSRF token held in memory; after login/verify we call `/firm/me` to load
 * the firm + role into AccountService. Mirrors the user app's cookie+CSRF pattern.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly account = inject(AccountService);
  private readonly firmApi = inject(FirmApiService);

  private readonly base = environment.apiBaseUrl;

  readonly csrfToken = signal<string>('');
  private csrfBootstrap: Promise<void> | null = null;

  /**
   * Dev-only Bearer fallback. In local http development the session cookie may be marked `Secure`
   * (when APP_BASE_URL is https) and get rejected over http, so the cookie alone can't authenticate.
   * The JWT strategy checks the Authorization header first, so we mirror the user app: keep the
   * access token in memory (+ sessionStorage to survive a reload) and attach it in dev. Production
   * authenticates purely via the httpOnly cookie — getAccessToken() returns null there.
   */
  private bearerToken: string | null = environment.production
    ? null
    : sessionStorage.getItem('acc_bearer');

  getAccessToken(): string | null {
    return environment.production ? null : this.bearerToken;
  }

  isAuthenticated(): boolean {
    return sessionStorage.getItem('acc_signed_in') === '1';
  }

  /** Fetch a CSRF token once (deduped) before a mutating request after reload. */
  async ensureCsrfReady(): Promise<void> {
    if (this.csrfToken()) return;
    if (!this.csrfBootstrap) {
      this.csrfBootstrap = firstValueFrom(
        this.http.get<{ data?: { csrf_token?: string } }>(`${this.base}/user/csrf-token`, {
          withCredentials: true,
        }),
      )
        .then((res) => {
          const t = res?.data?.csrf_token;
          if (t) this.csrfToken.set(t);
        })
        .catch(() => {})
        .finally(() => (this.csrfBootstrap = null));
    }
    return this.csrfBootstrap;
  }

  /**
   * Sign in, then load the firm context. Throws if the account isn't an accountant (no firm).
   * On a 2FA-enabled account the backend establishes no session here — it returns a login
   * challenge instead, and the caller must finish through `loginWith2FA`.
   */
  login(email: string, password: string): Observable<LoginOutcome> {
    return this.http
      .post<AuthEnvelope>(`${this.base}/user/login`, { email, password }, { withCredentials: true })
      .pipe(switchMap((res) => this.completeLogin(res, email)));
  }

  /**
   * "Continue with Google": the wrapper resolves a Google OAuth access token; the backend
   * re-verifies it with Google, then creates-or-signs-in the account. `loginOnly: true` means an
   * unknown Google account is refused (register instead) rather than silently created. Google
   * accounts have 2FA on by default, so this usually resolves to a 2FA challenge (step 2).
   */
  loginWithGoogle(accessToken: string): Observable<LoginOutcome> {
    return this.http
      .post<AuthEnvelope>(
        `${this.base}/user/login/google`,
        { accessToken, loginOnly: true },
        { withCredentials: true },
      )
      .pipe(switchMap((res) => this.completeLogin(res, '')));
  }

  /**
   * "Continue with Apple": trust-client — the wrapper decodes the Apple id_token for the stable id
   * + email and posts them; the backend creates-or-signs-in. Same `loginOnly` + 2FA behaviour as
   * Google.
   */
  loginWithApple(params: { email: string; appleId: string; name: string }): Observable<LoginOutcome> {
    return this.http
      .post<AuthEnvelope>(
        `${this.base}/user/login/apple`,
        { email: params.email, appleId: params.appleId, name: params.name, loginOnly: true },
        { withCredentials: true },
      )
      .pipe(switchMap((res) => this.completeLogin(res, params.email)));
  }

  /**
   * Shared tail for every sign-in path (password / Google / Apple): a 2FA account stops with a
   * challenge; otherwise absorb the session + load the firm. `fallbackEmail` seeds the challenge
   * when the response omits the email (social flows don't echo it back).
   */
  private completeLogin(res: AuthEnvelope, fallbackEmail: string): Observable<LoginOutcome> {
    const challenge = this.challengeFrom(res, fallbackEmail);
    if (challenge) return of<LoginOutcome>({ signedIn: false, challenge });
    this.absorb(res);
    return this.loadFirm().pipe(map((): LoginOutcome => ({ signedIn: true })));
  }

  /** Step 1 of a password reset: email the user a reset OTP. Silent whether or not the email exists. */
  requestPasswordReset(email: string): Observable<unknown> {
    return this.http.post(
      `${this.base}/user/password/reset/request`,
      { email },
      { withCredentials: true },
    );
  }

  /**
   * Step 2: verify the emailed OTP and set the new password in one call (the backend validates the
   * code here — there's no separate verify step). Does NOT sign the user in; they return to login.
   */
  resetPassword(params: {
    email: string;
    code: string;
    newPassword: string;
    confirmPassword: string;
  }): Observable<unknown> {
    return this.http.post(
      `${this.base}/user/password/reset`,
      {
        email: params.email,
        code: params.code,
        new_password: params.newPassword,
        confirm_password: params.confirmPassword,
      },
      { withCredentials: true },
    );
  }

  /**
   * Sign-in step 2 for a 2FA account: exchange the emailed code + challenge for the real session
   * cookie, then load the firm. The OTP alone is not enough — the challenge must accompany it.
   */
  loginWith2FA(params: { email: string; code: string; loginChallenge: string }): Observable<void> {
    return this.http
      .post<AuthEnvelope>(
        `${this.base}/user/login/2fa`,
        { email: params.email, twoFactorCode: params.code, loginChallenge: params.loginChallenge },
        { withCredentials: true },
      )
      .pipe(
        tap((res) => this.absorb(res)),
        switchMap(() => this.loadFirm()),
      );
  }

  /**
   * Hand the pending challenge from login step 1 to step 2. sessionStorage rather than a query
   * param so the short-lived token never lands in the URL or browser history.
   */
  stashTwoFactorChallenge(challenge: TwoFactorChallenge): void {
    sessionStorage.setItem(TWO_FACTOR_KEY, JSON.stringify(challenge));
  }

  /** The pending challenge, or null on a direct visit / reload of step 2. */
  readTwoFactorChallenge(): TwoFactorChallenge | null {
    try {
      const raw = sessionStorage.getItem(TWO_FACTOR_KEY);
      const parsed = raw ? (JSON.parse(raw) as TwoFactorChallenge) : null;
      return parsed?.loginChallenge && parsed?.email ? parsed : null;
    } catch {
      return null;
    }
  }

  clearTwoFactorChallenge(): void {
    sessionStorage.removeItem(TWO_FACTOR_KEY);
  }

  /** Re-send the 2FA email code. The challenge is reused as-is; only the countdown resets. */
  resendTwoFactorCode(params: {
    email: string;
    loginChallenge: string;
  }): Observable<TwoFactorChallenge> {
    return this.http
      .post<AuthEnvelope>(
        `${this.base}/user/login/2fa/resend`,
        { email: params.email, loginChallenge: params.loginChallenge },
        { withCredentials: true },
      )
      .pipe(
        map(
          (res) =>
            this.challengeFrom(res, params.email) ?? {
              ...params,
              codeExpiresIn: DEFAULT_CODE_TTL_SECONDS,
            },
        ),
      );
  }

  /**
   * Pull a 2FA challenge out of a step-1 response; null when that response signed the user in.
   * Detection is deliberately loose — camel and snake spellings, the metadata block, and a
   * message-only signal — because a missed 2FA response is the failure that sends a session-less
   * request to `/firm/me` and surfaces as a bogus "Authentication required" on the login form.
   */
  private challengeFrom(res: AuthEnvelope, email: string): TwoFactorChallenge | null {
    const data = (res?.data ?? {}) as Record<string, any>;
    const metadata = (res?.metadata ?? {}) as Record<string, any>;
    if (!this.twoFactorRequired(data, metadata)) return null;
    return {
      email: data['email'] ?? data['user']?.['email'] ?? email,
      // May be absent if 2FA was signalled by message alone — callers must guard before step 2.
      loginChallenge: data['loginChallenge'] ?? data['login_challenge'] ?? '',
      codeExpiresIn: resolveCodeTtl(data),
    };
  }

  private twoFactorRequired(data: Record<string, any>, metadata: Record<string, any>): boolean {
    const flagged = [
      data['requiresTwoFactor'],
      data['requires_two_factor'],
      data['twoFactorRequired'],
      data['two_factor_required'],
      metadata['requiresTwoFactor'],
      metadata['twoFactorRequired'],
    ].some((value) => value === true || value === 'true');
    if (flagged) return true;

    const message = String(metadata['message'] ?? data['message'] ?? '').toLowerCase();
    return (
      message.includes('two-factor') || message.includes('two factor') || message.includes('2fa')
    );
  }

  /** Step 1 of signup: create the user + trigger the email OTP. */
  register(payload: {
    name: string;
    email: string;
    password: string;
    confirm_password: string;
    timezone: string;
    country: string;
  }): Observable<unknown> {
    return this.http.post(`${this.base}/user/register`, payload, { withCredentials: true });
  }

  /**
   * Step 2 of signup: verify the OTP (logs the user in), then create their firm. `firmName` is the
   * person's name for a solo account (a firm of one).
   */
  verifyAndCreateFirm(params: {
    email: string;
    code: string;
    type: AccountType;
    firmName: string;
  }): Observable<void> {
    return this.http
      .post<AuthEnvelope>(
        `${this.base}/user/register/verify`,
        { email: params.email, code: params.code },
        { withCredentials: true },
      )
      .pipe(
        tap((res) => this.absorb(res)),
        switchMap(() =>
          this.firmApi.createFirm(params.firmName, params.type === 'solo' ? 'solo' : 'firm'),
        ),
        switchMap(() => this.loadFirm()),
      );
  }

  /** Accept a firm invite (caller must be signed in as the invited email), then load the firm. */
  acceptInvite(token: string): Observable<void> {
    return this.firmApi.acceptInvite(token).pipe(switchMap(() => this.loadFirm()));
  }

  /**
   * New-member onboarding: verify the OTP (logs the invited user in), then accept the invite and
   * load the firm. Used after a member registers from the accept-invite link.
   */
  verifyAndAcceptInvite(params: { email: string; code: string; token: string }): Observable<void> {
    return this.http
      .post<AuthEnvelope>(
        `${this.base}/user/register/verify`,
        { email: params.email, code: params.code },
        { withCredentials: true },
      )
      .pipe(
        tap((res) => this.absorb(res)),
        switchMap(() => this.firmApi.acceptInvite(params.token)),
        switchMap(() => this.loadFirm()),
      );
  }

  logout(): Observable<unknown> {
    const done = () => {
      sessionStorage.removeItem('acc_signed_in');
      this.clearTwoFactorChallenge();
      this.csrfToken.set('');
      this.bearerToken = null;
      sessionStorage.removeItem('acc_bearer');
      this.account.clear();
    };
    return this.http.post(`${this.base}/user/logout`, {}, { withCredentials: true }).pipe(
      tap({ next: done, error: done }),
    );
  }

  /** Read /firm/me and populate AccountService (firm name, type, role, user). */
  private loadFirm(): Observable<void> {
    return this.firmApi.me().pipe(
      map((me) => {
        this.account.set({
          type: me.firm.type,
          userName: this.account.userName() || me.firm.name,
          firmName: me.firm.name,
          email: this.account.email(),
          // The member's role decides what the workspace offers — without it every member would
          // get the owner's UI.
          role: me.role,
        });
        sessionStorage.setItem('acc_signed_in', '1');
      }),
    );
  }

  private absorb(res: AuthEnvelope): void {
    const token = res?.data?.csrf_token;
    if (token) this.csrfToken.set(token);
    // Dev Bearer fallback — never persisted in production (see getAccessToken).
    const accessToken = res?.data?.accessToken;
    if (accessToken && !environment.production) {
      this.bearerToken = accessToken;
      try {
        sessionStorage.setItem('acc_bearer', accessToken);
      } catch {
        /* ignore */
      }
    }
    const user = res?.data?.user;
    if (user) {
      // Seed name/email so loadFirm can keep them; firm name/type/role come from /firm/me.
      this.account.set({
        type: this.account.account().type,
        userName: user.name ?? this.account.userName(),
        firmName: this.account.firmName(),
        email: user.email ?? this.account.email(),
        role: this.account.role(),
      });
    }
  }
}
